// ============================================================
// TMR Portal Admin — criar e editar uma linha do molde global da TMR
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { SelectField, TextField } from '../../components/fields';
import { Icons } from '../../lib/icons';
import { bandLabel, rateTypeLabel, rateUnitSuffix } from '../../data/rateFormat';
import { useRateCatalog } from '../../data/rates/queries';
import { useCreateTmrRate, useTmrRateImpact, useUpdateTmrRate } from '../../data/globalRates/queries';
import {
  emptyTmrRateForm,
  toCreateTmrRateRequest,
  toTmrRateForm,
  toUpdateTmrRateRequest,
  validateTmrRateForm,
} from '../../data/globalRates/form';
import type { PropagationChoice, TmrRateForm, TmrRateFormErrors } from '../../data/globalRates/form';
import { impactSummary, missingTemplateWarning, saveSummary, tmrRateTitle } from '../../data/globalRates/status';
import type { TmrRate, TmrRateImpact } from '../../data/globalRates/contract';

/** Props for {@link TmrRateFormModal}. */
interface TmrRateFormModalProps {
  /** The template line being edited, or null to add one. */
  rate: TmrRate | null;
  /** Closes the modal. Carries the sentence the screen shows afterwards, when there is one. */
  onClose: (summary?: string) => void;
}

/**
 * The global template's form.
 *
 * What makes it different from the Empresa's rate form is the second half: **an edit asks who it reaches.**
 * Changing a line here decides what the next Empresa starts with; propagating it rewrites what live clinics
 * are charged, up to a thousand rows in the legacy. So the impact is counted before the choice is offered,
 * the choice has no default, and the form refuses to submit until it is made (`15` §3 A).
 *
 * It does not check band overlap, for the same reason the Empresa's form does not: the rule that decides
 * lives on the server (`rates.global_overlap`), and a second copy would eventually disagree with it. The
 * template is stricter about overlap than an Empresa's rates are — one ambiguous line here is copied into
 * every Empresa.
 */
export function TmrRateFormModal({ rate, onClose }: TmrRateFormModalProps) {
  const editing = rate !== null;
  const [form, setForm] = useState<TmrRateForm>(() => (rate ? toTmrRateForm(rate) : emptyTmrRateForm()));
  const [errors, setErrors] = useState<TmrRateFormErrors>({});

  const catalog = useRateCatalog();
  // Counted only for an edit: creating a line never reaches an Empresa that already exists, so there is no
  // radius to preview and no round trip to the legacy to spend.
  const impact = useTmrRateImpact(rate?.id);
  const create = useCreateTmrRate();
  const update = useUpdateTmrRate();
  const saving = create.isPending || update.isPending;
  const saveError = create.error ?? update.error;

  const set = <K extends keyof TmrRateForm>(key: K, value: TmrRateForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    const found = validateTmrRateForm(form, editing);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    if (rate) {
      const request = toUpdateTmrRateRequest(form);
      await update.mutateAsync({ rateId: rate.id, request });
      onClose(saveSummary(request.propagate, impact.data ?? null));
      return;
    }

    await create.mutateAsync(toCreateTmrRateRequest(form));
    onClose('Linha acrescentada ao molde. Ela vale para as empresas criadas a partir de agora.');
  };

  const rateTypeOptions = (catalog.data?.rateTypes ?? []).map((type) => ({
    value: String(type.id),
    label: rateTypeLabel(type.title),
  }));
  const engineOptions = (catalog.data?.creditEngineTypes ?? []).map((type) => ({
    value: String(type.id),
    label: type.title,
  }));

  return (
    <ModalShell
      title={editing ? 'Editar taxa do molde' : 'Nova taxa no molde'}
      lead={
        rate
          ? `${tmrRateTitle(rate)} · ${bandLabel(rate)}`
          : 'Uma linha nova vale para as empresas criadas daqui em diante.'
      }
      onClose={() => onClose()}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        {/* The vocabulary is the legacy's, read at runtime — a form that could not read it would be
            offering invented options. The select stays disabled rather than looking usable and empty. */}
        {catalog.isError && (
          <Notice tone="warning">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span>Não foi possível carregar os tipos de taxa agora.</span>
              <Btn
                variant="secondary"
                icon={Icons.swap}
                onClick={() => void catalog.refetch()}
                disabled={catalog.isFetching}
              >
                {catalog.isFetching ? 'Tentando…' : 'Tentar de novo'}
              </Btn>
            </div>
          </Notice>
        )}

        <SelectField
          label="Tipo da taxa"
          required
          value={form.rateTypeId}
          onChange={(value) => set('rateTypeId', value)}
          options={rateTypeOptions}
          placeholder={catalog.isPending ? 'Carregando…' : 'Escolha o tipo'}
          error={errors.rateTypeId}
          disabled={catalog.isPending || catalog.isError}
        />

        {engineOptions.length > 0 && (
          <SelectField
            label="Motor de aprovação"
            value={form.creditEngineTypeId}
            onChange={(value) => set('creditEngineTypeId', value)}
            options={engineOptions}
            placeholder="Todos"
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <TextField
            label="Parcela inicial"
            required
            inputMode="numeric"
            value={form.minInstallment}
            onChange={(value) => set('minInstallment', value)}
            error={errors.minInstallment}
          />
          <TextField
            label="Parcela final"
            required
            inputMode="numeric"
            value={form.maxInstallment}
            onChange={(value) => set('maxInstallment', value)}
            error={errors.maxInstallment}
          />
        </div>

        {/* The suffix comes from the rate type: Taxa de Saque and Operação de Crédito are amounts in
            reais, and this is the one screen that lists every type side by side (15 §5). */}
        <TextField
          label="Valor da taxa"
          required
          inputMode="decimal"
          value={form.value}
          onChange={(value) => set('value', value)}
          error={errors.value}
          suffix={form.rateTypeId ? rateUnitSuffix(Number(form.rateTypeId)) : undefined}
        />

        {editing && (
          <PropagationChoiceField
            value={form.propagate}
            onChange={(choice) => set('propagate', choice)}
            error={errors.propagate}
            impact={impact.data ?? null}
            loading={impact.isPending}
            failed={impact.isError}
          />
        )}

        {saveError && (
          <Notice tone="danger">
            {saveError instanceof ApiError ? saveError.detail : 'Não foi possível salvar a taxa.'}
          </Notice>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <Btn variant="secondary" onClick={() => onClose()} disabled={saving}>
          Cancelar
        </Btn>
        <Btn variant="primary" icon={Icons.check} onClick={() => void submit()} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar taxa'}
        </Btn>
      </div>
    </ModalShell>
  );
}

/** Props for {@link PropagationChoiceField}. */
interface PropagationChoiceFieldProps {
  value: PropagationChoice;
  onChange: (choice: PropagationChoice) => void;
  error?: string;
  impact: TmrRateImpact | null;
  loading: boolean;
  failed: boolean;
}

/**
 * The one control on this form that moves money: who the new value reaches.
 *
 * Two radios and no default. A checkbox — pre-checked or not — would make one of the answers the thing that
 * happens when nobody reads the question, and both answers are consequential here: not propagating leaves
 * every existing clinic on the old value, propagating rewrites all of them (`15` §12, 2026-08-14).
 *
 * The count sits **inside** the option it describes rather than above the pair, so the number is read as
 * part of choosing rather than as a heading somebody already scrolled past.
 */
function PropagationChoiceField({ value, onChange, error, impact, loading, failed }: PropagationChoiceFieldProps) {
  const missing = impact ? missingTemplateWarning(impact) : null;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <span className="pa-form-label">
        Esta mudança alcança as empresas que já existem?
        <span style={{ color: 'var(--danger)' }}> *</span>
      </span>

      {loading && (
        <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>Contando o alcance no sistema legado…</span>
      )}

      {/* The count failed, so the radius is unknown. The choice is still offered — refusing to let a
          template be edited because the legacy is unreachable would make the legacy a prerequisite for
          writing tmr_v2, which is exactly what the module was built not to be. */}
      {failed && (
        <Notice tone="warning">
          Não foi possível contar quantas empresas seriam alcançadas. Propagar sem esse número é uma escolha
          às cegas — dá para salvar sem propagar agora e propagar depois.
        </Notice>
      )}

      <Option
        selected={value === 'no'}
        onSelect={() => onChange('no')}
        title="Não — só para as próximas empresas"
        description="As empresas que já existem seguem com o valor que têm hoje."
      />

      <Option
        selected={value === 'yes'}
        onSelect={() => onChange('yes')}
        title="Sim — reescrever as empresas que já têm esta taxa"
        description={
          impact
            ? impactSummary(impact)
            : 'O novo valor passa a valer para as empresas que já têm uma taxa deste tipo e faixa.'
        }
        danger
      />

      {value === 'yes' && missing && <Notice tone="neutral">{missing}</Notice>}

      {error && <span style={{ fontSize: 12.5, color: 'var(--danger)' }}>{error}</span>}
    </div>
  );
}

/** One radio option, with the sentence that makes it a decision rather than a label. */
function Option({
  selected,
  onSelect,
  title,
  description,
  danger = false,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  danger?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '12px 14px',
        border: `1px solid ${selected ? (danger ? 'var(--danger)' : 'var(--border-strong)') : 'var(--border-default)'}`,
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        background: selected ? 'var(--cream-100)' : 'transparent',
      }}
    >
      <input
        type="radio"
        name="tmr-rate-propagation"
        checked={selected}
        onChange={onSelect}
        style={{ marginTop: 3 }}
      />
      <span style={{ fontSize: 13.5, lineHeight: 1.5 }}>
        <strong>{title}</strong>
        <br />
        <span style={{ color: 'var(--fg-muted)' }}>{description}</span>
      </span>
    </label>
  );
}
